import { googleAccessRefusalMessage, googleFaultMessage } from "./googleFaults";
import {
  CATALOG_TIMEOUT_CODE,
  CATALOG_TIMEOUT_MESSAGE,
  SOURCE_NOT_FOUND_MESSAGE,
  SOURCE_TIMEOUT_MESSAGE,
  sourceRefusedMessage,
  sourceUnreachableMessage,
} from "./noVerdict";

/**
 * #1097 (QA), interim until skypointcloud/skypoint-connector-hub#65 ships: the hub's SQL
 * validators answer `INVALID_CREDENTIALS` ("Invalid username or password") for a database that
 * does not exist and for a port that answers but is not the server's, as well as for a wrong user
 * name or password; and the extractor reads MySQL's 1044 ("Access denied ... to database", a
 * database name the login has no grant on, whether or not it exists) as AUTH. So for these
 * connectors no surface blames the sign-in alone: the validation card, the daily check's line,
 * badge and email, and an import's cause name every field that can cause it.
 *
 * The outcome is unchanged: the check stays `rejected` with cause `auth`, and `errorCode` is the
 * hub's own. A wrong password is still the commonest cause, and the alert is still worth sending.
 * Only the words change. Shared, so the web, the runner and the client read one rule.
 */

/** The connectors whose validator and extractor share the SQL error taxonomy. */
export const SQL_SIGN_IN_CONNECTORS: ReadonlySet<string> = new Set(["sqlserver", "mysql"]);

/** The hub's sign-in refusal code these validators overuse. */
export const SQL_SIGN_IN_REFUSAL_CODE = "INVALID_CREDENTIALS";

/** What the editor and the setup page say instead of "Invalid username or password". */
export const SQL_SIGN_IN_REFUSAL_MESSAGE =
  "Sign in failed, or the database name or port is wrong. Check the user name, password, database name and port.";

/** The daily check's cause, in the form of `CONNECTION_CHECK_SENTENCES` (no full stop). */
export const SQL_SIGN_IN_CHECK_SENTENCE =
  "The source refused the sign-in, or the database name or port is wrong";

/** The daily check's next step for these connectors. */
export const SQL_SIGN_IN_CHECK_STEP =
  "Check the user name, password, database name and port on the connection's Configuration tab, then select Validate.";

/** The badge in place of "Credentials refused". */
export const SQL_SIGN_IN_REFUSED_LABEL = "Sign in or settings refused";

/** True for a connector of the SQL taxonomy. */
export function isSqlSignInConnector(connectorName: string | null | undefined): boolean {
  return typeof connectorName === "string" && SQL_SIGN_IN_CONNECTORS.has(connectorName);
}

/**
 * The message a validation answer carries: the hub's own, except a SQL validator's sign-in
 * refusal, which is reworded so it never claims the password is the cause. Read when the answer
 * is built and again when a stored one is read, so an answer stored before #1097 reads the same.
 */
export function validationMessageFor(
  connectorName: string,
  answer: { ok: boolean; errorCode: string | null; message: string },
  settings: ValidationSettings = {},
): string {
  if (
    !answer.ok &&
    answer.errorCode === SQL_SIGN_IN_REFUSAL_CODE &&
    isSqlSignInConnector(connectorName)
  ) {
    return SQL_SIGN_IN_REFUSAL_MESSAGE;
  }
  // #1902 item 2: GA4 and Search Console's Skypoint-side Google faults say whose fault they are.
  if (!answer.ok) {
    const google = googleFaultMessage(connectorName, answer.errorCode);
    if (google !== null) return google;
    // #1902 item 1: an access refusal names the grant to make.
    const access = googleAccessRefusalMessage(connectorName, answer.errorCode, answer.message);
    if (access !== null) return access;
  }
  // SD2 (#1465): the hub's own timeout wording can read as a firewall diagnosis, so every caller
  // says the same two sentences.
  if (!answer.ok && answer.errorCode === CATALOG_TIMEOUT_CODE) return CATALOG_TIMEOUT_MESSAGE;
  if (!answer.ok && isSqlSignInConnector(connectorName)) {
    const reach = sqlReachMessage(connectorName, answer.errorCode, settings.port ?? null);
    if (reach !== null) return reach;
  }
  return answer.message;
}

/** #2439: what the caller knows of the connection the answer is about. */
export interface ValidationSettings {
  /** The connection's port (`portNumber`), when the caller has its fields. */
  port?: string | null;
}

/** #2439: the field both SQL seeds name their port by. */
export const SQL_PORT_FIELD_ID = "portNumber";

/**
 * #2439: a SQL validator's answer for a server it did not get to, worded by its cause. The hub's
 * own sentences blame the table count first, or carry a dash.
 */
function sqlReachMessage(
  connectorName: string,
  errorCode: string | null,
  port: string | null,
): string | null {
  switch (errorCode) {
    case "TIMEOUT":
      return connectorName === "sqlserver"
        ? sourceUnreachableMessage(port)
        : SOURCE_TIMEOUT_MESSAGE;
    case "HOST_UNREACHABLE":
      return SOURCE_NOT_FOUND_MESSAGE;
    case "CONNECTION_REFUSED":
      return sourceRefusedMessage(port);
    default:
      return null;
  }
}

/** #2439: the field both SQL seeds name their server by. */
export const SQL_SERVER_FIELD_ID = "serverName";

/**
 * #2439: the port a set of submitted or stored field values names, or null. A SQL Server named
 * instance (`host\INSTANCE`) is reached on the port its lookup answers, never `portNumber`, so
 * it names none.
 */
export function sqlPortOf(values: Readonly<Record<string, unknown>>): string | null {
  const server = values[SQL_SERVER_FIELD_ID];
  if (typeof server === "string" && server.includes("\\")) return null;
  const value = values[SQL_PORT_FIELD_ID];
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  if (typeof value === "string" && /^\d{1,5}$/.test(value.trim())) return value.trim();
  return null;
}

/**
 * Whether a connection's refused credential check reads as "sign-in or settings": a SQL
 * connector's check with cause `auth`. Any other connector or cause keeps its own words.
 */
export function isSqlSignInCheck(target: {
  targetType: string;
  connectorName?: string | null;
  causeCode?: string | null;
}): boolean {
  return (
    target.targetType === "dataflow" &&
    target.causeCode === "auth" &&
    isSqlSignInConnector(target.connectorName)
  );
}

/**
 * #1160: a run event's table cause as the run page reads it. A SQL connector's table refused
 * with the text-tier `auth` or `source_login_rejected` reads `sql_sign_in_rejected`, the step's
 * own cause, so one failure carries one name on one screen. Since #1160 the drain files the SQL
 * cause itself; this keeps an event stored before then in step. Codes only, never text.
 */
export function sqlSignInEventCauseCode(
  connectorName: string | null | undefined,
  causeCode: unknown,
): unknown {
  if (!isSqlSignInConnector(connectorName)) return causeCode;
  return causeCode === "auth" || causeCode === "source_login_rejected"
    ? "sql_sign_in_rejected"
    : causeCode;
}

/**
 * #1158: the two answers a database that could not be opened can carry. Which one a table reads
 * depends on whether tables of the connection had been read first, and the drain judges each
 * table when its message arrives, in no fixed order, while the step judges once from the whole
 * run. So a table event carrying either one reads its finished step's answer.
 */
const DATABASE_CAUSE_CODES: ReadonlySet<unknown> = new Set([
  "source_database_offline",
  "source_database_not_opened",
]);

/**
 * A run event's payload with its table cause read by `sqlSignInEventCauseCode`, and, when its
 * connection's extract step has finished with a database cause (`stepCauseCode`, the step's
 * `metrics.importCause.code`), a table's database cause read as the step's (#1158). Codes only.
 */
export function withSqlSignInEventCause(
  connectorName: string | null | undefined,
  payload: unknown,
  stepCauseCode: unknown = null,
): unknown {
  if (typeof payload !== "object" || payload === null || Array.isArray(payload)) return payload;
  const record = payload as Record<string, unknown>;
  if (record.causeSource !== "connector") return payload;
  let code = sqlSignInEventCauseCode(connectorName, record.causeCode);
  if (DATABASE_CAUSE_CODES.has(code) && DATABASE_CAUSE_CODES.has(stepCauseCode)) {
    code = stepCauseCode;
  }
  return code === record.causeCode ? payload : { ...record, causeCode: code };
}
